// POS → Restaurant → Salón (/pos/rest-salon).
// Facturación visual por áreas con las mesas dibujadas, operada por TECLADO:
//   · Teclear el NÚMERO de mesa + Enter la selecciona (vía principal, como
//     Aloha/Micros); flechas navegan, Tab cambia de área.
//   · Enter abre/entra a la cuenta · F3 para llevar · F5 pre-cuenta ·
//     F6 transferir mesa · F7 unir cuentas · F8 sucia/limpia · F9 cobrar.
// La cuenta es PERSISTENTE (rest_cuentas): rondas a cocina (comanda impresa por
// estación + KDS), anulación con PIN de supervisor, pre-cuenta con 10% y cobro
// vía pos_registrar_venta (factura SRI + arqueo del POS). Ver scripts 317/318.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { uuid } from '@/lib/uuid';
import { ChefHat, CreditCard, Loader2, LogIn, Maximize2, Minimize2, Printer, Search, Send, UtensilsCrossed } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { SelectorLocal, Campo, Modal, fieldCls, money, pos as posRest, posRpc, productoThumbs, redondearMoneda, useImpresionConfig } from './shared';
import type { Caja, FormaPago } from './shared';
import { BuscarCliente } from './PuntoVenta';
import { CobroPasarela } from './CobroPasarela';
import { type PasarelaModos, pasarelaModos } from './shared';
import { ventaATicket, type TicketRpc } from './VentasDia';
import { imprimirComanda, imprimirPrecuenta, imprimirTicket } from './print';
import { hotelRpc, type HuespedEnCasa } from '@/features/hotel/shared';
import {
  CURSOS, CuentaT, EsperaT, GRID_COLS, GRID_ROWS, MESA_CLS, MESA_LABEL, MesaT, MeseroSesionT,
  ModGrupoT, RepartidorT, RestEstadoT, cobradaOfflineMarcar, cobradasOffline, cobradasOfflineLimpiar,
  elementoVisible, estadoMesa, restCuentaOffline, restEstadoQuery, snapshotRestCuenta,
  useConexion, useLocalRestaurant,
} from './restaurante';
import {
  asegurarBloque, bloqueDisponible, claveOffline, encolarVenta, formasOffline,
  sincronizarVentas, snapshotFormas, ventasPendientes,
} from './offline';
import { LOCALE } from '@/lib/utils/regional';

const INV = { headers: { 'Content-Profile': 'inventario', 'Accept-Profile': 'inventario' } };

export interface ProductoBusqueda {
  id: string; codigo: string; nombre: string; precio_base: number; iva_tarifa: number;
  stock: number; controla_stock: boolean; descuento_pct: number;
}
interface PagoRow {
  key: number; forma_pago_id: string; monto: string; referencia: string;
  // Datos de la tarjeta: `rest_cuenta_cobrar` los pasa tal cual a
  // `pos_registrar_venta`, así que el salón también puede conciliarse.
  adquirente?: string; tarjeta_autorizacion?: string; tarjeta_ultimos4?: string;
  tarjeta_marca?: string; tarjeta_lote?: string; tarjeta_terminal?: string; tarjeta_voucher?: string;
  /** La pasarela ya cobró esta línea: salir sin cobrar la deja huérfana. */
  pasarela_ok?: boolean;
}


export function Salon() {
  useImpresionConfig();  // deja lista la config del ticket (CFG) antes de imprimir
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const rootRef = useRef<HTMLDivElement>(null);
  const { locationId, setLocationId, locations } = useLocalRestaurant();

  // Persistencia ante Ctrl+R: el mesero (PIN validado) vive en sessionStorage
  // y la vista (área/cuenta abierta) en localStorage — la CUENTA en sí ya es
  // persistente en el servidor.
  const [mesero, setMesero] = useState<MeseroSesionT | null>(() => {
    try { return JSON.parse(sessionStorage.getItem('rest_mesero') ?? 'null'); } catch { return null; }
  });
  const [areaIdx, setAreaIdx] = useState(() => Number(localStorage.getItem('rest_area_idx') ?? 0));
  const [selMesa, setSelMesa] = useState<string | null>(null);
  const [buffer, setBuffer] = useState('');
  const [cuentaId, setCuentaId] = useState<string | null>(() => localStorage.getItem('rest_cuenta_abierta') || null);
  const [selLinea, setSelLinea] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const online = useConexion();
  const [colaPend, setColaPend] = useState(0);
  const [cobradasOff, setCobradasOff] = useState<Record<string, string>>({});

  // modales
  const [mPin, setMPin] = useState(false);
  const [mAbrir, setMAbrir] = useState<{ mesa: MesaT | null; llevar: boolean } | null>(null);
  const [mBuscar, setMBuscar] = useState(false);
  const [mMods, setMMods] = useState<{ prod: ProductoBusqueda; grupos: ModGrupoT[]; curso: number; asiento: number } | null>(null);
  const [mAnular, setMAnular] = useState<string | null>(null);   // linea id
  const [mTransferir, setMTransferir] = useState(false);
  const [mUnir, setMUnir] = useState(false);
  const [mCobro, setMCobro] = useState(false);
  const [mEspera, setMEspera] = useState(false);

  const { data: estado } = useQuery<RestEstadoT>(restEstadoQuery(locationId));
  const { data: repartidores = [] } = useQuery<RepartidorT[]>({
    queryKey: ['rest-repartidores'],
    queryFn: () => posRpc<RepartidorT[]>('rest_repartidores_listar', {}),
  });
  const { data: espera = [] } = useQuery<EsperaT[]>({
    queryKey: ['rest-espera', locationId],
    queryFn: () => posRpc<EsperaT[]>('rest_espera_listar', { _location: locationId }),
    enabled: !!locationId,
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 15000,
  });
  const esperando = espera.filter((e) => e.estado === 'esperando');
  const { data: cuenta } = useQuery<CuentaT | null>({
    queryKey: ['rest-cuenta', cuentaId],
    queryFn: async () => {
      try {
        const c = await posRpc<CuentaT | null>('rest_cuenta', { _id: cuentaId });
        if (c) void snapshotRestCuenta(c);
        return c;
      } catch (e) {
        const cached = await restCuentaOffline(cuentaId!);
        if (cached) return cached;
        throw e;
      }
    },
    enabled: !!cuentaId,
    staleTime: 0, refetchOnMount: 'always', refetchInterval: 5000,
  });

  // persistir la vista + sesión del mesero (Ctrl+R no pierde nada)
  useEffect(() => {
    if (mesero) sessionStorage.setItem('rest_mesero', JSON.stringify(mesero));
    else sessionStorage.removeItem('rest_mesero');
  }, [mesero]);
  useEffect(() => { localStorage.setItem('rest_area_idx', String(areaIdx)); }, [areaIdx]);
  useEffect(() => {
    if (cuentaId) localStorage.setItem('rest_cuenta_abierta', cuentaId);
    else localStorage.removeItem('rest_cuenta_abierta');
  }, [cuentaId]);

  const refrescarCola = useCallback(async () => {
    const cola = await ventasPendientes();
    setColaPend(cola.length);
    setCobradasOff(await cobradasOffline());
  }, []);
  useEffect(() => { void refrescarCola(); }, [refrescarCola, online]);

  // al reconectar: sincroniza la cola (idempotente) y refresca el salón
  useEffect(() => {
    if (!online) return;
    void (async () => {
      const cola = await ventasPendientes();
      if (!cola.length) return;
      const r = await sincronizarVentas();
      if (r.ok > 0 || r.fallidas > 0) {
        const quedan = await ventasPendientes();
        const sincronizadas = cola.filter((v) => !quedan.some((q) => q.client_uuid === v.client_uuid))
          .map((v) => v.client_uuid);
        await cobradasOfflineLimpiar(sincronizadas);
        await refrescarCola();
        void qc.invalidateQueries({ queryKey: ['rest-estado'] });
        void qc.invalidateQueries({ queryKey: ['rest-cuenta'] });
        toast.success(`Sincronización: ${r.ok} cobro(s) enviados${r.fallidas ? ` · ${r.fallidas} con error` : ''}`);
      }
    })();
  }, [online, qc, refrescarCola]);

  // preparación offline (con red): snapshot de formas del local + bloque de claves de la caja
  useEffect(() => {
    if (!online || !locationId) return;
    void (async () => {
      try {
        const formas = await posRpc<FormaPago[]>('pos_formas_pago_local', { _location_id: locationId });
        await snapshotFormas(locationId, formas);
      } catch { /* sin red */ }
      const cajaId = localStorage.getItem('pos_rest_caja');
      if (cajaId) {
        try { await asegurarBloque(cajaId, localStorage.getItem('pos_device_id') ?? 'rest-terminal'); }
        catch { /* la caja puede no tener emisión configurada */ }
      }
    })();
  }, [online, locationId]);

  const areas = estado?.areas ?? [];
  const area = areas[Math.min(areaIdx, Math.max(areas.length - 1, 0))];
  const mesasArea = useMemo(
    () => (estado?.mesas ?? []).filter((m) => m.area_id === area?.id),
    [estado, area]);

  // El número de mesa es único POR SALA (697): resolver primero en la sala
  // activa; si no está ahí y existe en UNA sola otra sala, usar esa; si se
  // repite en varias, es ambiguo (hay que ir a la sala y tocarla).
  const mesaPorNumero = useCallback((numero: number) => {
    const todas = estado?.mesas ?? [];
    const enArea = todas.find((m) => m.area_id === area?.id && m.numero === numero);
    if (enArea) return { mesa: enArea, ambigua: false };
    const otras = todas.filter((m) => m.numero === numero);
    if (otras.length === 1) return { mesa: otras[0], ambigua: false };
    return { mesa: null, ambigua: otras.length > 1 };
  }, [estado, area]);

  const invalidar = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['rest-estado'] });
    void qc.invalidateQueries({ queryKey: ['rest-cuenta'] });
  }, [qc]);

  const rpc = useCallback(async <T,>(fn: string, body: Record<string, unknown>): Promise<T | null> => {
    if (!conexionOnlineRef.current) {
      toast.error('🔌 Sin conexión: solo se puede COBRAR en efectivo (F9); el resto al volver la red');
      return null;
    }
    try {
      const r = await posRpc<T>(fn, body);
      invalidar();
      return r;
    } catch (e) { toast.error(getErrorMessage(e)); return null; }
  }, [invalidar]);
  const conexionOnlineRef = useRef(online);
  useEffect(() => { conexionOnlineRef.current = online; }, [online]);

  // ---- acciones ----
  const abrirCuenta = async (mesa: MesaT | null, tipo: string, comensales: number,
                             entrega: { nombre: string; telefono: string; direccion: string } | null) => {
    if (!mesero) { setMPin(true); return; }
    try {
      const c = await posRpc<CuentaT>('rest_cuenta_abrir', {
        _location: locationId, _mesa: mesa?.id ?? null, _tipo: mesa ? 'mesa' : tipo,
        _pin: mesero.pin, _comensales: comensales, _entrega: entrega,
      });
      invalidar();
      if (c) { setMAbrir(null); setCuentaId(c.id); }
    } catch (e) {
      const msg = getErrorMessage(e);
      if (/PIN de mesero inválido/i.test(msg)) {
        // La sesión guardada quedó con un PIN viejo (lo cambiaron en Meseros,
        // o desactivaron al mesero): cerrarla y pedir el PIN de nuevo.
        setMesero(null); setMAbrir(null); setMPin(true);
        toast.error('Tu PIN de mesero cambió o ya no es válido — vuelve a ingresarlo');
      } else {
        toast.error(msg);
      }
    }
  };

  const enviarRonda = async () => {
    if (!cuentaId) return;
    const r = await rpc<{ ronda: number; curso: number; retenidas: number;
      cuenta: { codigo: string; mesa_numero: number | null; tipo: string; mesero: string | null };
      estaciones: { estacion: string; imprime_comanda: boolean; modo_impresion?: string; lineas: { cantidad: number; descripcion: string; modificadores: { nombre: string }[] | null; notas: string | null; asiento?: number | null }[] }[] }>(
      'rest_ronda_enviar', { _cuenta: cuentaId });
    if (!r) return;
    for (const est of r.estaciones) {
      // las estaciones con impresora de RED las imprime el print-agent
      if (est.imprime_comanda && est.modo_impresion !== 'red') {
        imprimirComanda({ estacion: est.estacion, ronda: r.ronda, codigo: r.cuenta.codigo,
          mesaNumero: r.cuenta.mesa_numero, tipo: r.cuenta.tipo, mesero: r.cuenta.mesero, lineas: est.lineas });
      }
    }
    toast.success(`Ronda ${r.ronda} · curso ${r.curso} a cocina${Number(r.retenidas) > 0
      ? ` · ${r.retenidas} línea(s) de cursos siguientes RETENIDAS (F3 = fire)` : ''}`);
  };

  const fireCurso = async () => {
    if (!cuentaId) return;
    const r = await rpc<{ curso: number; lineas: number;
      cuenta: { codigo: string; mesa_numero: number | null; tipo: string; mesero: string | null };
      estaciones: { estacion: string; imprime_comanda: boolean; modo_impresion?: string; lineas: { cantidad: number; descripcion: string; modificadores: { nombre: string }[] | null; notas: string | null; asiento?: number | null }[] }[] }>(
      'rest_curso_fire', { _cuenta: cuentaId, _curso: null });
    if (!r) return;
    for (const est of r.estaciones) {
      if (est.imprime_comanda && est.modo_impresion !== 'red') {
        imprimirComanda({ estacion: est.estacion, ronda: 0, codigo: r.cuenta.codigo,
          mesaNumero: r.cuenta.mesa_numero, tipo: r.cuenta.tipo, mesero: r.cuenta.mesero, lineas: est.lineas });
      }
    }
    toast.success(`FIRE curso ${r.curso}: ${r.lineas} línea(s) a cocina`);
  };

  const precuenta = async () => {
    if (!cuentaId) return;
    const r = await rpc<{ empresa: string; cuenta: CuentaT }>('rest_precuenta', { _cuenta: cuentaId });
    if (!r) return;
    const cu = r.cuenta;
    imprimirPrecuenta({
      empresa: r.empresa, codigo: cu.codigo, mesaNumero: cu.mesa_numero, area: cu.area,
      mesero: cu.mesero, comensales: cu.comensales,
      lineas: cu.lineas.filter((l) => l.estado !== 'anulada' && !l.venta_id)
        .map((l) => ({ cantidad: l.cantidad, descripcion: l.descripcion, subtotal: l.subtotal, modificadores: l.modificadores })),
      subtotal: cu.totales.subtotal, servicio: cu.totales.servicio, servicioPct: cu.servicio_pct,
      iva: cu.totales.iva, total: cu.totales.total,
    });
  };

  // ---- teclado ----
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!elementoVisible(rootRef.current)) return;
      const modalAbierto = mPin || !!mAbrir || mBuscar || !!mMods || !!mAnular || mTransferir || mUnir || mCobro;
      if (modalAbierto) return; // cada modal maneja sus teclas
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (cuentaId) {
        // vista CUENTA
        if (e.key === 'Escape') { e.preventDefault(); setCuentaId(null); return; }
        if (e.key === 'F1') { e.preventDefault(); setMBuscar(true); return; }
        if (e.key === 'F3') { e.preventDefault(); void fireCurso(); return; }
        if (e.key === 'F4') { e.preventDefault(); void enviarRonda(); return; }
        if (e.key === 'F5') { e.preventDefault(); void precuenta(); return; }
        if (e.key === 'F9') {
          e.preventDefault();
          if (cobradasOff[cuentaId]) { toast.error('Esta cuenta ya fue cobrada OFFLINE: espera la sincronización'); return; }
          setMCobro(true); return;
        }
        if (e.key === 'ArrowUp') { e.preventDefault(); setSelLinea((i) => Math.max(0, i - 1)); return; }
        if (e.key === 'ArrowDown') { e.preventDefault(); setSelLinea((i) => i + 1); return; }
        if (e.key === 'F6' || e.key === 'Delete') {
          e.preventDefault();
          const ls = (cuenta?.lineas ?? []).filter((l) => l.estado !== 'anulada' && !l.venta_id);
          const l = ls[Math.min(selLinea, ls.length - 1)];
          if (!l) return;
          if (l.estado === 'pendiente') void rpc('rest_linea_quitar', { _linea: l.id });
          else setMAnular(l.id);
          return;
        }
        return;
      }
      // vista PLANO
      if (/^\d$/.test(e.key)) { e.preventDefault(); setBuffer((b) => (b + e.key).slice(0, 4)); return; }
      if (e.key === 'Backspace') { e.preventDefault(); setBuffer((b) => b.slice(0, -1)); return; }
      if (e.key === 'Tab') { e.preventDefault(); setAreaIdx((i) => (areas.length ? (i + 1) % areas.length : 0)); return; }
      if (e.key === 'Escape') { e.preventDefault(); setBuffer(''); setSelMesa(null); return; }
      if (e.key === 'F3') { e.preventDefault(); setMAbrir({ mesa: null, llevar: true }); return; }
      const mesaSel = (estado?.mesas ?? []).find((m) => m.id === selMesa) ?? null;
      if (e.key === 'Enter' || e.key === 'F2') {
        e.preventDefault();
        if (buffer) {
          const { mesa: m, ambigua } = mesaPorNumero(Number(buffer));
          setBuffer('');
          if (ambigua) { toast.error(`La mesa ${buffer} existe en varias salas — cambia a su sala (Tab) y ábrela ahí`); return; }
          if (!m) { toast.error(`No existe la mesa ${buffer}`); return; }
          const ai = areas.findIndex((a) => a.id === m.area_id);
          if (ai >= 0) setAreaIdx(ai);
          setSelMesa(m.id);
          if (m.cuenta) setCuentaId(m.cuenta.id);
          else setMAbrir({ mesa: m, llevar: false });
          return;
        }
        if (!mesaSel) return;
        if (mesaSel.cuenta) setCuentaId(mesaSel.cuenta.id);
        else setMAbrir({ mesa: mesaSel, llevar: false });
        return;
      }
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
        if (!mesasArea.length) return;
        const orden = [...mesasArea].sort((a, b) => a.pos_y - b.pos_y || a.pos_x - b.pos_x);
        const idx = Math.max(0, orden.findIndex((m) => m.id === selMesa));
        const next = e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? Math.max(0, idx - 1) : Math.min(orden.length - 1, idx + 1);
        setSelMesa(orden[next].id);
        return;
      }
      if (!mesaSel) return;
      if (e.key === 'F5') { e.preventDefault(); if (mesaSel.cuenta) { setCuentaId(mesaSel.cuenta.id); setTimeout(() => void precuenta(), 300); } return; }
      if (e.key === 'F6') { e.preventDefault(); if (mesaSel.cuenta) setMTransferir(true); return; }
      if (e.key === 'F7') { e.preventDefault(); if (mesaSel.cuenta) setMUnir(true); return; }
      if (e.key === 'F8') {
        e.preventDefault();
        if (mesaSel.cuenta) return;
        void rpc('rest_mesa_estado_manual', { _mesa: mesaSel.id, _estado: mesaSel.estado_manual === 'sucia' ? null : 'sucia' });
        return;
      }
      if (e.key === 'F9') { e.preventDefault(); if (mesaSel.cuenta) { setCuentaId(mesaSel.cuenta.id); setMCobro(true); } return; }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estado, areas, mesasArea, selMesa, buffer, cuentaId, cuenta, selLinea,
      mPin, mAbrir, mBuscar, mMods, mAnular, mTransferir, mUnir, mCobro]);

  const toggleFs = () => {
    if (document.fullscreenElement) { void document.exitFullscreen(); setFullscreen(false); }
    else { void rootRef.current?.requestFullscreen?.(); setFullscreen(true); }
  };

  const lineasActivas = (cuenta?.lineas ?? []).filter((l) => l.estado !== 'anulada' && !l.venta_id);

  return (
    <div ref={rootRef} className="p-4 space-y-3 bg-background min-h-full">
      {dlg.ui}
      {/* barra superior */}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5" /> Salón
        </h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
        <button className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-sm"
                onClick={() => setMPin(true)} title="Cambiar de mesero (PIN)">
          <LogIn className="w-4 h-4" />
          {mesero ? <>Mesero: <span className="font-medium">{mesero.nombre}</span>{mesero.es_supervisor && ' ★'}</> : 'Ingresar PIN de mesero'}
        </button>
        {buffer && <span className="px-2 py-1 rounded bg-primary/10 text-primary font-mono text-lg">Mesa: {buffer}▌</span>}
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${online
          ? (colaPend > 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300')
          : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 animate-pulse'}`}
          title={online ? 'Conectado' : 'Sin conexión: plano en modo lectura, cobro solo efectivo'}>
          {online ? (colaPend > 0 ? `Sincronizando (${colaPend})…` : 'En línea') : `OFFLINE${colaPend ? ` · ${colaPend} en cola` : ''}`}
        </span>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span>Nº+Enter mesa · Tab área · F3 llevar · F5 pre-cuenta · F6 transferir · F7 unir · F8 sucia · F9 cobrar</span>
          <Button size="sm" variant="ghost" onClick={toggleFs} title="Modo desktop (pantalla completa)">
            {fullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {!cuentaId && (
        <>
          {/* pestañas de áreas */}
          <div className="flex items-center gap-1 flex-wrap">
            {areas.map((a, i) => (
              <button key={a.id}
                className={`px-3 py-1.5 rounded-t-md text-sm border-b-2 ${i === areaIdx
                  ? 'border-primary font-semibold text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                onClick={() => setAreaIdx(i)}>{a.nombre}</button>
            ))}
            {!areas.length && (
              <span className="text-sm text-muted-foreground">
                Este local no tiene áreas: créalas en POS → Restaurant → Áreas y Mesas.
              </span>
            )}
            {(estado?.llevar ?? []).length > 0 && (
              <div className="ml-auto flex gap-1 flex-wrap">
                {(estado?.llevar ?? []).map((c) => (
                  <button key={c.id} onClick={() => setCuentaId(c.id)}
                    className={`px-2 py-1 rounded-md border text-xs ${c.tipo === 'delivery'
                      ? 'bg-fuchsia-50 dark:bg-fuchsia-900/30 border-fuchsia-400'
                      : 'bg-sky-50 dark:bg-sky-900/30 border-sky-400'}`}>
                    {c.tipo} {c.codigo} · {money(c.total)} · {c.minutos}m
                    {c.tipo === 'delivery' && (
                      <> · {c.entrega_estado ?? 'pendiente'}{c.repartidor ? ` (${c.repartidor})` : ''}</>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* plano */}
          <div className="relative border rounded-lg bg-muted/20 overflow-auto bg-center bg-cover"
               style={{ minHeight: '60vh', ...(area?.imagen_fondo ? { backgroundImage: `url(${area.imagen_fondo})` } : {}) }}>
            <div className="grid gap-1 p-3"
                 style={{ gridTemplateColumns: `repeat(${GRID_COLS}, minmax(28px, 1fr))`,
                          gridTemplateRows: `repeat(${GRID_ROWS}, minmax(34px, 1fr))` }}>
              {mesasArea.map((m) => {
                const est = estadoMesa(m);
                const sel = m.id === selMesa;
                return (
                  <button key={m.id}
                    className={`border-2 flex flex-col items-center justify-center leading-tight transition-shadow
                      ${MESA_CLS[est]} ${m.forma === 'circulo' ? 'rounded-full' : 'rounded-lg'}
                      ${sel ? 'ring-4 ring-primary shadow-lg' : ''}`}
                    style={{ gridColumn: `${m.pos_x} / span ${m.ancho}`, gridRow: `${m.pos_y} / span ${m.alto}` }}
                    onClick={() => { setSelMesa(m.id); if (m.cuenta) setCuentaId(m.cuenta.id); else setMAbrir({ mesa: m, llevar: false }); }}
                    title={`${MESA_LABEL[est]} · capacidad ${m.capacidad}`}>
                    <span className="font-bold text-xl">{m.numero}</span>
                    {m.cuenta && cobradasOff[m.cuenta.id] ? (
                      <span className="text-[10px] text-center font-medium">✓ cobrada offline<br />por sincronizar</span>
                    ) : m.cuenta ? (
                      <span className="text-[10px] text-center">
                        {m.cuenta.minutos}m · {money(m.cuenta.total)}<br />{m.cuenta.mesero ?? ''}
                      </span>
                    ) : m.reserva ? (
                      <span className="text-[10px] text-center">
                        ⏰ {m.reserva.hora}<br />{m.reserva.cliente} ({m.reserva.personas})
                      </span>
                    ) : (
                      <span className="text-[10px]">{MESA_LABEL[est]}</span>
                    )}
                  </button>
                );
              })}
            </div>
            {!mesasArea.length && area && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
                El área «{area.nombre}» no tiene mesas.
              </div>
            )}
          </div>

          {/* leyenda */}
          <div className="flex gap-3 flex-wrap text-xs">
            {(Object.keys(MESA_LABEL) as (keyof typeof MESA_LABEL)[]).map((k) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span className={`w-3 h-3 rounded border-2 inline-block ${MESA_CLS[k]}`} /> {MESA_LABEL[k]}
              </span>
            ))}
            <Button size="sm" variant="outline" className="ml-auto" onClick={() => setMEspera(true)}>
              ⏳ Lista de espera{esperando.length > 0 && (
                <span className="ml-1.5 px-1.5 rounded-full bg-amber-500 text-white text-xs">{esperando.length}</span>
              )}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMAbrir({ mesa: null, llevar: true })}>
              <Send className="w-4 h-4 mr-1" /> Para llevar (F3)
            </Button>
          </div>
        </>
      )}

      {/* vista CUENTA */}
      {cuentaId && cuenta && (
        <div className="grid gap-3 lg:grid-cols-3">
          <div className="lg:col-span-2 border rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold text-lg">
                {cuenta.mesa_numero != null ? `Mesa ${cuenta.mesa_numero}` : cuenta.tipo.toUpperCase()} · {cuenta.codigo}
              </span>
              <span className="text-sm text-muted-foreground">
                {cuenta.area ?? ''} · {cuenta.mesero ?? 'sin mesero'} · {cuenta.comensales} pers. · {cuenta.minutos} min
              </span>
              <Button size="sm" variant="ghost" className="ml-auto" onClick={() => setCuentaId(null)}>← Plano (Esc)</Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" onClick={() => setMBuscar(true)}><Search className="w-4 h-4 mr-1" /> F1 Agregar</Button>
              <Button size="sm" variant="outline" onClick={() => void enviarRonda()}
                      disabled={!lineasActivas.some((l) => l.estado === 'pendiente')}>
                <ChefHat className="w-4 h-4 mr-1" /> F4 Enviar a cocina
              </Button>
              {lineasActivas.some((l) => l.estado === 'retenida') && (
                <Button size="sm" variant="outline"
                        className="border-amber-500 text-amber-700 dark:text-amber-400"
                        onClick={() => void fireCurso()}>
                  🔥 F3 Fire curso {Math.min(...lineasActivas.filter((l) => l.estado === 'retenida').map((l) => l.curso ?? 1))}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => void precuenta()} disabled={!lineasActivas.length}>
                <Printer className="w-4 h-4 mr-1" /> F5 Pre-cuenta
              </Button>
              <Button size="sm" onClick={() => setMCobro(true)}
                      disabled={!lineasActivas.length || !!cobradasOff[cuenta.id]}>
                <CreditCard className="w-4 h-4 mr-1" /> F9 Cobrar
              </Button>
              {cobradasOff[cuenta.id] && (
                <span className="text-xs font-medium text-amber-600 dark:text-amber-400 self-center">
                  ✓ Cobrada OFFLINE — se cierra al sincronizar
                </span>
              )}
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-1.5 pr-2 text-left w-14">Ronda</th>
                  <th className="py-1.5 pr-2 text-left">Detalle</th>
                  <th className="py-1.5 pr-2 text-right">Cant.</th>
                  <th className="py-1.5 pr-2 text-right">P. unit.</th>
                  <th className="py-1.5 pr-2 text-right">Subtotal</th>
                  <th className="py-1.5 pr-2 text-center">Estado</th>
                </tr>
              </thead>
              <tbody>
                {lineasActivas.map((l, i) => (
                  <tr key={l.id}
                      className={`border-b last:border-0 cursor-pointer ${i === Math.min(selLinea, lineasActivas.length - 1) ? 'bg-primary/10' : ''}`}
                      onClick={() => setSelLinea(i)}>
                    <td className="py-1.5 pr-2 whitespace-nowrap">
                      {l.ronda ?? <span className="text-amber-600 font-medium">nueva</span>}
                      {(l.curso ?? 1) > 1 && <span className="ml-1 text-[10px] px-1 rounded bg-muted">C{l.curso}</span>}
                      {l.asiento != null && <span className="ml-1 text-[10px] px-1 rounded bg-primary/10 text-primary">A{l.asiento}</span>}
                    </td>
                    <td className="py-1.5 pr-2">
                      <div>
                        {l.descripcion}
                        {l.origen === 'qr' && <span className="ml-1.5 text-[10px] px-1 rounded bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300">📱 QR</span>}
                        {l.origen === 'agregador' && <span className="ml-1.5 text-[10px] px-1 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">🛵 App</span>}
                      </div>
                      {(l.modificadores ?? []).length > 0 && (
                        <div className="text-xs text-muted-foreground">• {(l.modificadores ?? []).map((m) => m.nombre).join(' · ')}</div>
                      )}
                      {l.notas && <div className="text-xs text-amber-700 dark:text-amber-400">✎ {l.notas}</div>}
                    </td>
                    <td className="py-1.5 pr-2 text-right">{l.cantidad}</td>
                    <td className="py-1.5 pr-2 text-right font-mono tabular-nums">{money(l.precio_unitario)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono tabular-nums">{money(l.subtotal)}</td>
                    <td className="py-1.5 pr-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-[11px] ${l.estado === 'pendiente'
                        ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                        : l.estado === 'retenida' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                        : l.estado === 'lista' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-muted text-muted-foreground'}`}>{l.estado === 'retenida' ? `retenida C${l.curso}` : l.estado}</span>
                    </td>
                  </tr>
                ))}
                {!lineasActivas.length && (
                  <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">
                    Cuenta vacía: F1 para agregar platos.
                  </td></tr>
                )}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground">
              F6/Supr: quitar línea nueva o ANULAR comandada (PIN de supervisor). Las líneas ya cobradas no se muestran.
            </p>
          </div>

          <div className="border rounded-lg p-3 space-y-2 h-fit">
            <div className="font-semibold">Totales</div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{money(cuenta.totales.subtotal)}</span></div>
              {cuenta.servicio_pct > 0 && (
                <div className="flex justify-between"><span>Servicio {cuenta.servicio_pct}%</span><span className="font-mono">{money(cuenta.totales.servicio)}</span></div>
              )}
              <div className="flex justify-between"><span>IVA</span><span className="font-mono">{money(cuenta.totales.iva)}</span></div>
              <div className="flex justify-between font-bold text-lg border-t pt-1"><span>TOTAL</span><span className="font-mono">{money(cuenta.totales.total)}</span></div>
            </div>
            {cuenta.tipo === 'delivery' && (
              <div className="border-t pt-2 space-y-2 text-sm">
                <div className="font-semibold">Entrega</div>
                <div className="text-xs text-muted-foreground">
                  {cuenta.entrega_nombre ?? ''} {cuenta.entrega_telefono ? `· ${cuenta.entrega_telefono}` : ''}<br />
                  {cuenta.entrega_direccion}
                </div>
                <select className={fieldCls} value={cuenta.repartidor_id ?? ''}
                        onChange={(e) => void rpc('rest_entrega_set', { _cuenta: cuenta.id, _repartidor: e.target.value || null, _estado: null })}>
                  <option value="">— Repartidor —</option>
                  {repartidores.filter((r) => r.activo).map((r) => <option key={r.id} value={r.id}>{r.nombre}</option>)}
                </select>
                <div className="flex gap-1.5">
                  {(['pendiente', 'despachado', 'entregado'] as const).map((es) => (
                    <button key={es}
                      className={`px-2 py-0.5 rounded-full border text-xs capitalize ${cuenta.entrega_estado === es
                        ? 'bg-primary text-primary-foreground border-primary' : ''}`}
                      onClick={() => void rpc('rest_entrega_set', { _cuenta: cuenta.id, _repartidor: null, _estado: es })}>
                      {es}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="border-t pt-2 space-y-1">
              <Button size="sm" variant="outline" className="w-full" onClick={() => setMTransferir(true)}>Transferir de mesa (F6)</Button>
              <Button size="sm" variant="outline" className="w-full" onClick={() => setMUnir(true)}>Unir a otra mesa (F7)</Button>
              <Button size="sm" variant="ghost" className="w-full text-red-600" onClick={async () => {
                const v = await dlg.pedir('Cancelar la cuenta', [
                  { key: 'pin', label: 'PIN de supervisor', minimo: 0 },
                ], { detalle: 'Solo hace falta si tiene líneas comandadas.', aceptar: 'Cancelar cuenta' });
                if (v === null) return;
                const r = await rpc('rest_cuenta_cancelar', { _cuenta: cuentaId, _pin_supervisor: v.pin || null });
                if (r) { setCuentaId(null); toast.success('Cuenta cancelada'); }
              }}>Cancelar cuenta</Button>
            </div>
          </div>
        </div>
      )}

      {cuentaId && !cuenta && <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}

      {/* ------- modales ------- */}
      {mPin && <PinModal onClose={() => setMPin(false)} onOk={(m) => { setMesero(m); setMPin(false); }} />}
      {mAbrir && (
        <AbrirModal mesa={mAbrir.mesa} llevar={mAbrir.llevar} mesero={mesero}
          onPin={() => setMPin(true)} onClose={() => setMAbrir(null)}
          onOk={(comensales, tipo, entrega) => void abrirCuenta(mAbrir.mesa, tipo, comensales, entrega)} />
      )}
      {mBuscar && cuentaId && (
        <BuscarModal locationId={locationId} comensales={cuenta?.comensales ?? 4} onClose={() => setMBuscar(false)}
          onPick={async (p, curso, asiento) => {
            const grupos = await posRpc<ModGrupoT[]>('rest_producto_mods', { _producto: p.id });
            setMBuscar(false);
            if (grupos.length) setMMods({ prod: p, grupos, curso, asiento });
            else {
              await rpc('rest_linea_agregar', { _cuenta: cuentaId, _producto: p.id, _cantidad: 1,
                _precio_base: p.precio_base, _modificadores: [], _notas: null, _curso: curso, _asiento: asiento || null });
            }
          }} />
      )}
      {mMods && cuentaId && (
        <ModsModal prod={mMods.prod} grupos={mMods.grupos} cursoInicial={mMods.curso}
          asientoInicial={mMods.asiento} comensales={cuenta?.comensales ?? 4} onClose={() => setMMods(null)}
          onOk={async (cantidad, opciones, notas, curso, asiento) => {
            const r = await rpc('rest_linea_agregar', { _cuenta: cuentaId, _producto: mMods.prod.id,
              _cantidad: cantidad, _precio_base: mMods.prod.precio_base,
              _modificadores: opciones.map((o) => ({ opcion_id: o })), _notas: notas || null,
              _curso: curso, _asiento: asiento || null });
            if (r) setMMods(null);
          }} />
      )}
      {mAnular && (
        <AnularModal onClose={() => setMAnular(null)} onOk={async (pin, motivo) => {
          const r = await rpc<{ estacion: string | null; descripcion: string; cantidad: number; ronda: number | null; comandada: boolean }>(
            'rest_linea_anular', { _linea: mAnular, _pin_supervisor: pin, _motivo: motivo });
          if (r) {
            setMAnular(null);
            if (r.comandada && cuenta) {
              imprimirComanda({ estacion: r.estacion ?? 'General', ronda: r.ronda ?? 0, codigo: cuenta.codigo,
                mesaNumero: cuenta.mesa_numero, tipo: cuenta.tipo, mesero: cuenta.mesero,
                lineas: [{ cantidad: r.cantidad, descripcion: r.descripcion }], anulada: true });
            }
            toast.success('Línea anulada (auditado)');
          }
        }} />
      )}
      {mTransferir && cuenta && (
        <NumeroMesaModal titulo={`Transferir ${cuenta.codigo} a la mesa…`} onClose={() => setMTransferir(false)}
          onOk={async (numero) => {
            const { mesa: m, ambigua } = mesaPorNumero(numero);
            if (ambigua) { toast.error(`La mesa ${numero} existe en varias salas — ábrela desde su sala`); return; }
            if (!m) { toast.error(`No existe la mesa ${numero}`); return; }
            const r = await rpc('rest_mesa_transferir', { _cuenta: cuenta.id, _mesa: m.id });
            if (r) { setMTransferir(false); toast.success(`Cuenta movida a la mesa ${numero}`); }
          }} />
      )}
      {mUnir && cuenta && (
        <NumeroMesaModal titulo={`Unir ${cuenta.codigo} a la cuenta de la mesa…`} onClose={() => setMUnir(false)}
          onOk={async (numero) => {
            const { mesa: m, ambigua } = mesaPorNumero(numero);
            if (ambigua) { toast.error(`La mesa ${numero} existe en varias salas — ábrela desde su sala`); return; }
            if (!m?.cuenta) { toast.error(`La mesa ${numero} no tiene cuenta abierta`); return; }
            const r = await rpc('rest_cuentas_unir', { _origen: cuenta.id, _destino: m.cuenta.id });
            if (r) { setMUnir(false); setCuentaId(m.cuenta.id); toast.success('Cuentas unidas'); }
          }} />
      )}
      {mCobro && cuenta && (
        <CobroModal cuenta={cuenta} locationId={locationId} onClose={() => setMCobro(false)}
          onDone={(cerrada) => { setMCobro(false); if (cerrada) setCuentaId(null); invalidar(); void refrescarCola(); }} />
      )}
      {mEspera && (
        <EsperaModal locationId={locationId} espera={espera} mesas={estado?.mesas ?? []}
          onClose={() => setMEspera(false)}
          onRefresh={() => void qc.invalidateQueries({ queryKey: ['rest-espera'] })} />
      )}
    </div>
  );
}

function EsperaModal({ locationId, espera, mesas, onClose, onRefresh }: {
  locationId: string; espera: EsperaT[]; mesas: MesaT[];
  onClose: () => void; onRefresh: () => void;
}) {
  const dlg = useDialogoEnLinea();
  const [nombre, setNombre] = useState('');
  const [personas, setPersonas] = useState('2');
  const [telefono, setTelefono] = useState('');
  const llamar = async (fn: string, body: Record<string, unknown>, ok: string) => {
    try { await posRpc(fn, body); onRefresh(); toast.success(ok); }
    catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <Modal title="Lista de espera" onClose={onClose}>
      {dlg.ui}
      <div className="space-y-3">
        <div className="max-h-64 overflow-auto divide-y border rounded-md">
          {espera.filter((e) => e.estado === 'esperando').map((e, i) => (
            <div key={e.id} className="px-3 py-2 flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">{i + 1}.</span>
              <span className="flex-1">
                <span className="font-medium">{e.nombre}</span> · {e.personas} pers.
                {e.telefono ? ` · ${e.telefono}` : ''} ·{' '}
                <span className={e.minutos > 20 ? 'text-red-600 font-medium' : 'text-muted-foreground'}>{e.minutos} min</span>
              </span>
              <Button size="sm" variant="outline" onClick={async () => {
                const v = await dlg.pedir(`Sentar a ${e.nombre}`, [
                  { key: 'numero', label: 'N.º de mesa', tipo: 'number' },
                ], { detalle: 'Indica el número de mesa.' });
                const n = v?.numero ?? '';
                if (!n) return;
                const mesa = mesas.find((m) => m.numero === Number(n));
                if (!mesa) { toast.error(`No existe la mesa ${n}`); return; }
                void llamar('rest_espera_estado', { _id: e.id, _estado: 'sentado', _mesa: mesa.id },
                  `${e.nombre} → mesa ${n} (abre la cuenta con Nº+Enter)`);
              }}>Sentar</Button>
              <Button size="sm" variant="ghost" title="Se fue"
                      onClick={() => void llamar('rest_espera_estado', { _id: e.id, _estado: 'se_fue', _mesa: null }, 'Marcado: se fue')}>
                ✕
              </Button>
            </div>
          ))}
          {!espera.some((e) => e.estado === 'esperando') && (
            <div className="px-3 py-4 text-center text-sm text-muted-foreground">Nadie en espera.</div>
          )}
        </div>
        <div className="grid grid-cols-[1fr_70px_1fr_auto] gap-2 items-end">
          <Campo label="Nombre">
            <input className={fieldCls} value={nombre} onChange={(e) => setNombre(e.target.value)} />
          </Campo>
          <Campo label="Pers.">
            <input className={fieldCls} type="number" min="1" value={personas} onChange={(e) => setPersonas(e.target.value)} />
          </Campo>
          <Campo label="Teléfono">
            <input className={fieldCls} value={telefono} onChange={(e) => setTelefono(e.target.value)} />
          </Campo>
          <Button size="sm" disabled={!nombre.trim()} onClick={() => {
            void llamar('rest_espera_agregar', { _location: locationId,
              _data: { nombre: nombre.trim(), personas: Number(personas) || 2, telefono } }, 'Agregado a la espera');
            setNombre(''); setTelefono(''); setPersonas('2');
          }}>Agregar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// PinModal/AbrirModal/BuscarModal/ModsModal/AnularModal/CobroModal se exportan:
// el modo MESERO MÓVIL (MeseroMovil.tsx) reutiliza estos mismos modales.
export function PinModal({ onClose, onOk }: { onClose: () => void; onOk: (m: MeseroSesionT) => void }) {
  const [pin, setPin] = useState('');
  const [cargando, setCargando] = useState(false);
  const validar = async () => {
    setCargando(true);
    try {
      const r = await posRpc<{ id: string; nombre: string; es_supervisor: boolean } | null>('rest_pin_validar', { _pin: pin });
      if (!r) { toast.error('PIN inválido'); return; }
      onOk({ ...r, pin });
      toast.success(`Hola, ${r.nombre}`);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setCargando(false); }
  };
  return (
    <Modal title="PIN de mesero" onClose={onClose}>
      <div className="space-y-3">
        <input autoFocus className={`${fieldCls} text-center text-2xl tracking-[0.5em] font-mono`} type="password"
               inputMode="numeric" maxLength={6} value={pin}
               onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
               onKeyDown={(e) => { if (e.key === 'Enter' && pin.length >= 4) void validar(); }} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => void validar()} isLoading={cargando} disabled={pin.length < 4}>Entrar</Button>
        </div>
      </div>
    </Modal>
  );
}

export function AbrirModal({ mesa, llevar, mesero, onPin, onClose, onOk }: {
  mesa: MesaT | null; llevar: boolean; mesero: MeseroSesionT | null;
  onPin: () => void; onClose: () => void;
  onOk: (comensales: number, tipo: string, entrega: { nombre: string; telefono: string; direccion: string } | null) => void;
}) {
  const [comensales, setComensales] = useState('2');
  const [tipo, setTipo] = useState<'llevar' | 'delivery' | 'barra'>('llevar');
  const [eNombre, setENombre] = useState('');
  const [eTelefono, setETelefono] = useState('');
  const [eDireccion, setEDireccion] = useState('');
  const esDelivery = !mesa && tipo === 'delivery';
  const confirmar = () => onOk(Math.max(1, Number(comensales) || 1), tipo,
    esDelivery ? { nombre: eNombre, telefono: eTelefono, direccion: eDireccion } : null);
  return (
    <Modal title={llevar ? 'Nueva cuenta (llevar / delivery / barra)' : `Abrir mesa ${mesa?.numero}`} onClose={onClose}>
      <div className="space-y-3">
        {!mesero && (
          <p className="text-sm text-amber-600 dark:text-amber-400">
            Necesitas identificarte con tu PIN de mesero. <button className="underline" onClick={onPin}>Ingresar PIN</button>
          </p>
        )}
        {!mesa && (
          <div className="flex gap-1.5">
            {(['llevar', 'delivery', 'barra'] as const).map((t) => (
              <button key={t} onClick={() => setTipo(t)}
                className={`px-3 py-1 rounded-full border text-sm capitalize ${tipo === t ? 'bg-primary text-primary-foreground border-primary' : ''}`}>
                {t}
              </button>
            ))}
          </div>
        )}
        {mesa && (
          <Campo label="Comensales">
            <input autoFocus className={fieldCls} type="number" min="1" value={comensales}
                   onChange={(e) => setComensales(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter' && mesero) confirmar(); }} />
          </Campo>
        )}
        {esDelivery && (
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Cliente (nombre)">
              <input className={fieldCls} value={eNombre} onChange={(e) => setENombre(e.target.value)} />
            </Campo>
            <Campo label="Teléfono">
              <input className={fieldCls} value={eTelefono} onChange={(e) => setETelefono(e.target.value)} />
            </Campo>
            <Campo label="Dirección de entrega *" className="col-span-2">
              <input className={fieldCls} value={eDireccion} onChange={(e) => setEDireccion(e.target.value)} />
            </Campo>
          </div>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!mesero || (esDelivery && !eDireccion.trim())} onClick={confirmar}>
            Abrir cuenta{mesero ? ` (${mesero.nombre})` : ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function BuscarModal({ locationId, comensales, onClose, onPick }: {
  locationId: string; comensales: number; onClose: () => void;
  onPick: (p: ProductoBusqueda, curso: number, asiento: number) => void;
}) {
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const [curso, setCurso] = useState(1);
  const [asiento, setAsiento] = useState(0);
  const { data: rows = [] } = useQuery<ProductoBusqueda[]>({
    queryKey: ['rest-buscar', locationId, q],
    queryFn: async () => (await apiClient.post('/rpc/pos_buscar_productos',
      { p_q: q, p_location: locationId, p_partner: null }, INV)).data,
    enabled: q.length >= 2,
    staleTime: 0,
  });
  const ids = useMemo(() => rows.map((p) => p.id), [rows]);
  const { data: thumbs = {} } = useQuery<Record<string, string>>({
    queryKey: ['rest-pick-thumbs', ids.join(',')],
    queryFn: () => productoThumbs(ids), enabled: ids.length > 0, staleTime: 5 * 60 * 1000,
  });
  return (
    <Modal title="Agregar producto (↑/↓ + Enter)" onClose={onClose}>
      <div className="space-y-2">
        <input autoFocus className={fieldCls} placeholder="Nombre o código…" value={q}
               onChange={(e) => { setQ(e.target.value); setSel(0); }}
               onKeyDown={(e) => {
                 if (e.key === 'ArrowDown') { e.preventDefault(); setSel((i) => Math.min(i + 1, rows.length - 1)); }
                 if (e.key === 'ArrowUp') { e.preventDefault(); setSel((i) => Math.max(i - 1, 0)); }
                 if (e.key === 'Enter' && rows[sel]) { e.preventDefault(); onPick(rows[sel], curso, asiento); }
                 if (e.key === 'F2') { e.preventDefault(); setCurso((c) => (c % 3) + 1); }
                 if (e.key === 'F3') { e.preventDefault(); setAsiento((a) => (a + 1) % (comensales + 1)); }
                 if (e.key === 'Escape') onClose();
               }} />
        <div className="flex items-center gap-1.5 text-xs flex-wrap">
          <span className="text-muted-foreground">Curso (F2):</span>
          {CURSOS.map((c) => (
            <button key={c.v} onClick={() => setCurso(c.v)}
              className={`px-2 py-0.5 rounded-full border ${curso === c.v ? 'bg-primary text-primary-foreground border-primary' : ''}`}>
              {c.l}
            </button>
          ))}
          <span className="text-muted-foreground ml-2">Asiento (F3):</span>
          <button onClick={() => setAsiento(0)}
            className={`px-2 py-0.5 rounded-full border ${asiento === 0 ? 'bg-primary text-primary-foreground border-primary' : ''}`}>—</button>
          {Array.from({ length: Math.min(comensales, 9) }, (_, i) => i + 1).map((a) => (
            <button key={a} onClick={() => setAsiento(a)}
              className={`px-2 py-0.5 rounded-full border ${asiento === a ? 'bg-primary text-primary-foreground border-primary' : ''}`}>
              {a}
            </button>
          ))}
        </div>
        <div className="max-h-72 overflow-auto border rounded-md divide-y">
          {rows.map((p, i) => (
            <button key={p.id} className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${i === sel ? 'bg-primary/10' : ''}`}
                    onClick={() => onPick(p, curso, asiento)}>
              {thumbs[p.id]
                ? <img src={thumbs[p.id]} alt="" className="w-8 h-8 rounded object-cover flex-none" />
                : <UtensilsCrossed className="w-8 h-8 p-1.5 rounded bg-muted text-muted-foreground/50 flex-none" />}
              <span className="flex-1">{p.nombre} <span className="text-xs text-muted-foreground">{p.codigo}</span></span>
              <span className="font-mono whitespace-nowrap">{money(p.precio_base)}</span>
            </button>
          ))}
          {q.length >= 2 && !rows.length && <div className="px-3 py-4 text-sm text-muted-foreground text-center">Sin resultados</div>}
        </div>
      </div>
    </Modal>
  );
}

export function ModsModal({ prod, grupos, cursoInicial, asientoInicial, comensales, onClose, onOk }: {
  prod: ProductoBusqueda; grupos: ModGrupoT[]; cursoInicial: number; asientoInicial: number;
  comensales: number; onClose: () => void;
  onOk: (cantidad: number, opciones: string[], notas: string, curso: number, asiento: number) => void;
}) {
  const [sel, setSel] = useState<Record<string, string[]>>({});
  const [cantidad, setCantidad] = useState('1');
  const [notas, setNotas] = useState('');
  const [curso, setCurso] = useState(cursoInicial);
  const [asiento, setAsiento] = useState(asientoInicial);
  const toggle = (g: ModGrupoT, op: string) => {
    const gid = (g.grupo_id ?? g.id)!;
    setSel((s) => {
      const cur = s[gid] ?? [];
      if (cur.includes(op)) return { ...s, [gid]: cur.filter((x) => x !== op) };
      const next = g.max_sel <= 1 ? [op] : [...cur, op].slice(-g.max_sel);
      return { ...s, [gid]: next };
    });
  };
  const faltantes = grupos.filter((g) => (sel[(g.grupo_id ?? g.id)!] ?? []).length < g.min_sel);
  const opciones = Object.values(sel).flat();
  const extra = grupos.flatMap((g) => g.opciones).filter((o) => opciones.includes(o.id))
    .reduce((a, o) => a + Number(o.precio_extra), 0);
  return (
    <Modal title={prod.nombre} onClose={onClose}>
      <div className="space-y-3">
        {grupos.map((g) => (
          <div key={(g.grupo_id ?? g.id)!}>
            <div className="text-sm font-medium mb-1">
              {g.nombre} {g.min_sel > 0 && <span className="text-red-500">*</span>}
              <span className="text-xs text-muted-foreground"> (elige {g.min_sel === g.max_sel ? g.max_sel : `${g.min_sel}-${g.max_sel}`})</span>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {g.opciones.map((o) => {
                const on = (sel[(g.grupo_id ?? g.id)!] ?? []).includes(o.id);
                return (
                  <button key={o.id} onClick={() => toggle(g, o.id)}
                    className={`px-2.5 py-1 rounded-full border text-sm ${on ? 'bg-primary text-primary-foreground border-primary' : ''}`}>
                    {o.nombre}{Number(o.precio_extra) > 0 && ` +${money(o.precio_extra)}`}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div className="grid grid-cols-4 gap-3">
          <Campo label="Cantidad">
            <input className={fieldCls} type="number" min="1" value={cantidad} onChange={(e) => setCantidad(e.target.value)} />
          </Campo>
          <Campo label="Curso">
            <select className={fieldCls} value={curso} onChange={(e) => setCurso(Number(e.target.value))}>
              {CURSOS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
          </Campo>
          <Campo label="Asiento">
            <select className={fieldCls} value={asiento} onChange={(e) => setAsiento(Number(e.target.value))}>
              <option value={0}>— compartido —</option>
              {Array.from({ length: Math.min(comensales, 9) }, (_, i) => i + 1).map((a) => (
                <option key={a} value={a}>Asiento {a}</option>
              ))}
            </select>
          </Campo>
          <Campo label="Nota a cocina">
            <input className={fieldCls} maxLength={200} placeholder="p.ej. sin sal" value={notas} onChange={(e) => setNotas(e.target.value)} />
          </Campo>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">Precio: <span className="font-mono font-semibold">{money(prod.precio_base + extra)}</span></span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button disabled={faltantes.length > 0}
                    onClick={() => onOk(Math.max(1, Number(cantidad) || 1), opciones, notas, curso, asiento)}>
              Agregar
            </Button>
          </div>
        </div>
        {faltantes.length > 0 && (
          <p className="text-xs text-red-500">Faltan obligatorios: {faltantes.map((g) => g.nombre).join(', ')}</p>
        )}
      </div>
    </Modal>
  );
}

export function AnularModal({ onClose, onOk }: { onClose: () => void; onOk: (pin: string, motivo: string) => void }) {
  const [pin, setPin] = useState('');
  const [motivo, setMotivo] = useState('error');
  return (
    <Modal title="Anular línea comandada (supervisor)" onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-muted-foreground">
          La línea ya fue enviada a cocina: la anulación exige PIN de SUPERVISOR, queda auditada e imprime
          un ticket «ANULADO» en la estación.
        </p>
        <Campo label="PIN de supervisor">
          <input autoFocus className={`${fieldCls} font-mono`} type="password" inputMode="numeric" maxLength={6}
                 value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} />
        </Campo>
        <Campo label="Motivo">
          <select className={fieldCls} value={motivo} onChange={(e) => setMotivo(e.target.value)}>
            <option value="error">Error de digitación</option>
            <option value="devuelto">Cliente lo devolvió</option>
            <option value="cortesia">Cortesía</option>
            <option value="otro">Otro</option>
          </select>
        </Campo>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" disabled={pin.length < 4} onClick={() => onOk(pin, motivo)}>Anular</Button>
        </div>
      </div>
    </Modal>
  );
}

function NumeroMesaModal({ titulo, onClose, onOk }: {
  titulo: string; onClose: () => void; onOk: (numero: number) => void;
}) {
  const [n, setN] = useState('');
  return (
    <Modal title={titulo} onClose={onClose}>
      <div className="space-y-3">
        <input autoFocus className={`${fieldCls} text-center text-2xl font-mono`} inputMode="numeric" value={n}
               onChange={(e) => setN(e.target.value.replace(/\D/g, ''))}
               onKeyDown={(e) => { if (e.key === 'Enter' && n) onOk(Number(n)); }} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!n} onClick={() => onOk(Number(n))}>Aceptar</Button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
export function CobroModal({ cuenta, locationId, onClose, onDone }: {
  cuenta: CuentaT; locationId: string; onClose: () => void; onDone: (cerrada: boolean) => void;
}) {
  const online = useConexion();
  const [cajaId, setCajaId] = useState(() => localStorage.getItem('pos_rest_caja') ?? '');
  const [clienteId, setClienteId] = useState('');
  const [clienteNombre, setClienteNombre] = useState('');
  const [buscarCli, setBuscarCli] = useState(false);
  const [tipoDoc, setTipoDoc] = useState<'factura' | 'nota_venta'>('factura');
  const [dividir, setDividir] = useState(false);
  const [lineasSel, setLineasSel] = useState<string[]>([]);
  const [partesOn, setPartesOn] = useState(!!cuenta.partes);
  const [partesN, setPartesN] = useState(String(cuenta.partes ?? 2));
  const [pagos, setPagos] = useState<PagoRow[]>([]);
  // Cobro con tarjeta por pasarela (scripts 879-880). El salón se configura
  // aparte del mostrador: aquí el QR en la mesa es lo natural.
  const [pasarelaPagoKey, setPasarelaPagoKey] = useState<number | null>(null);
  const { data: modosPasarela } = useQuery<PasarelaModos>({
    queryKey: ['pos-pasarela-modos', 'restaurant'],
    queryFn: () => pasarelaModos('restaurant'), staleTime: 5 * 60 * 1000,
  });
  const [propina, setPropina] = useState('');
  const [cobrando, setCobrando] = useState(false);

  const { data: cajas = [] } = useQuery<Caja[]>({
    queryKey: ['rest-cajas'],
    queryFn: () => posRest.list<Caja>('cajas', '?select=id,codigo,nombre,location_id,centro_costo_id,establecimiento,punto_emision,ambiente,es_activo&es_activo=eq.true&order=nombre'),
  });
  const { data: formas = [] } = useQuery<FormaPago[]>({
    queryKey: ['rest-formas', locationId],
    queryFn: async () => {
      try {
        const f = await posRpc<FormaPago[]>('pos_formas_pago_local', { _location_id: locationId });
        void snapshotFormas(locationId, f);
        return f;
      } catch (e) {
        const cached = await formasOffline(locationId);
        if (cached.length) return cached;
        throw e;
      }
    },
  });
  const { data: sesion } = useQuery<{ id: string; codigo: string } | null>({
    queryKey: ['rest-sesion', cajaId],
    queryFn: () => posRpc<{ id: string; codigo: string } | null>('pos_sesion_activa', { p_caja_id: cajaId }),
    enabled: !!cajaId, staleTime: 0, refetchOnMount: 'always',
  });
  const { data: cfData } = useQuery({
    queryKey: ['pos-cf-rest'],
    queryFn: async () => {
      try {
        const cf = await posRpc<{ id: string }>('pos_consumidor_final', {});
        localStorage.setItem('pos_rest_cf', cf.id);
        return cf;
      } catch (e) {
        const cached = localStorage.getItem('pos_rest_cf');
        if (cached) return { id: cached };
        throw e;
      }
    },
    staleTime: 0, refetchOnMount: 'always',
  });
  // Rellena el cliente por defecto (CF) en CADA apertura del modal, aunque la
  // query esté cacheada — evita que la 2ª parte quede sin cliente y falle con
  // "Seleccione el cliente".
  useEffect(() => {
    const cf = cfData?.id ?? localStorage.getItem('pos_rest_cf');
    if (cf) {
      setClienteId((c) => c || cf);
      setClienteNombre((n) => n || 'CONSUMIDOR FINAL');
    }
  }, [cfData]);
  useEffect(() => { if (cajaId) localStorage.setItem('pos_rest_caja', cajaId); }, [cajaId]);

  // Cargo a la habitación (módulo Hotel, 921): solo con red (el folio/huésped se
  // valida en el servidor) y cuenta SIN dividir — se carga la cuenta completa al
  // folio del huésped; se cobra en el check-out. `catch` deja el módulo invisible
  // si la empresa no tiene hotel configurado o el esquema no está desplegado.
  const { data: hotelCfg } = useQuery<{ activo?: boolean } | null>({
    queryKey: ['pos-hotel-config'], queryFn: () => hotelRpc<{ activo?: boolean } | null>('config_get').catch(() => null), staleTime: 5 * 60 * 1000,
  });
  const hotelDisponible = !!hotelCfg && online && !cuenta.partes;
  const [modoCobro, setModoCobro] = useState<'pago' | 'habitacion'>('pago');
  const [buscaHuesped, setBuscaHuesped] = useState('');
  const [huespedSel, setHuespedSel] = useState<HuespedEnCasa | null>(null);
  useEffect(() => { if (!hotelDisponible && modoCobro === 'habitacion') setModoCobro('pago'); }, [hotelDisponible, modoCobro]);
  const { data: huespedes = [], isLoading: huespedesLoading } = useQuery<HuespedEnCasa[]>({
    queryKey: ['pos-hotel-huespedes-en-casa'], queryFn: () => hotelRpc<HuespedEnCasa[]>('huespedes_en_casa'),
    enabled: modoCobro === 'habitacion', staleTime: 0,
  });
  const huespedesFiltrados = useMemo(() => {
    const q = buscaHuesped.trim().toLowerCase();
    if (!q) return huespedes;
    return huespedes.filter((h) => h.habitacion.toLowerCase().includes(q) || h.huesped.toLowerCase().includes(q) || h.codigo.toLowerCase().includes(q));
  }, [huespedes, buscaHuesped]);

  const lineas = cuenta.lineas.filter((l) => l.estado !== 'anulada' && !l.venta_id);
  const seleccion = dividir ? lineas.filter((l) => lineasSel.includes(l.id)) : lineas;
  // Cobro completo: los totales del SERVIDOR mandan (espejo exacto de la RPC).
  // División por ítem: preview local con el mismo redondeo half-up del server.
  const subSel = dividir ? seleccion.reduce((a, l) => a + Number(l.subtotal), 0) : Number(cuenta.totales.subtotal);
  const srvSel = dividir ? Math.round(subSel * Number(cuenta.servicio_pct)) / 100 : Number(cuenta.totales.servicio);
  const ivaSel = dividir
    ? seleccion.reduce((a, l) => a + Math.round(Number(l.subtotal) * Number(l.iva_tarifa)) / 100, 0)
    : Number(cuenta.totales.iva);
  const totalCompleto = dividir ? Math.round((subSel + srvSel + ivaSel) * 100) / 100 : Number(cuenta.totales.total);
  // Partes iguales: preview de la parte que toca (el server calcula el exacto
  // por tarifa; la última parte lleva el remanente)
  const nPartes = Math.max(2, Number(partesN) || 2);
  const parteActual = (cuenta.partes_cobradas ?? 0) + 1;
  const parteBase = Math.round((Number(cuenta.totales.total) / nPartes) * 100) / 100;
  const montoParte = parteActual === nPartes
    ? Math.round((Number(cuenta.totales.total) - parteBase * (nPartes - 1)) * 100) / 100
    : parteBase;
  const totalSel = partesOn ? montoParte : totalCompleto;
  // Igual que en el POS: con la tarjeta ya cobrada, salir sin cobrar la cuenta
  // deja el cobro huérfano — el cliente pagó y no hay factura que lo respalde.
  const hayCobroPasarela = pagos.some((p) => p.pasarela_ok);
  const AVISO_NO_SALIR =
    'La tarjeta YA fue cobrada. Cobra la cuenta para que el pago quede unido a la factura — '
    + 'si sales ahora, el cobro queda huérfano.';
  const intentarCerrar = () => {
    if (hayCobroPasarela) { toast.error(AVISO_NO_SALIR, { duration: 9000 }); return; }
    onClose();
  };

  useEffect(() => {
    if (formas.length && !pagos.length) {
      setPagos([{ key: 1, forma_pago_id: formas[0].id, monto: String(totalSel.toFixed(2)), referencia: '' }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formas.length]);
  useEffect(() => {
    setPagos((ps) => ps.length === 1 ? [{ ...ps[0], monto: totalSel.toFixed(2) }] : ps);
  }, [totalSel]);

  // COBRO OFFLINE (script 323): cuenta completa, solo efectivo; se encola con
  // client_uuid (replay idempotente vía rest_cuenta_cobrar) y clave SRI del
  // bloque reservado de la caja si es factura.
  const cobrarOffline = async () => {
    if (!cajaId) { toast.error('Elige la caja'); return; }
    const cli = clienteId || localStorage.getItem('pos_rest_cf');
    if (!cli) { toast.error('Sin cliente en caché (abre el cobro una vez con red)'); return; }
    const pagosValidos = pagos.filter((p) => Number(p.monto) > 0);
    const soloEfectivo = pagosValidos.every((p) => formas.find((f) => f.id === p.forma_pago_id)?.tipo === 'efectivo');
    if (!pagosValidos.length || !soloEfectivo) { toast.error('Sin conexión solo se acepta EFECTIVO'); return; }
    setCobrando(true);
    try {
      let offline: { secuencial: number; clave_acceso: string } | null = null;
      let serie: string | null = null;
      if (tipoDoc === 'factura') {
        const c = await claveOffline(cajaId);
        if (!c) {
          const disp = await bloqueDisponible(cajaId);
          toast.error(disp === 0
            ? 'Sin bloque de claves SRI reservado para esta caja: cobra como NOTA DE VENTA'
            : 'No se pudo tomar la clave offline');
          setCobrando(false); return;
        }
        offline = { secuencial: c.secuencial, clave_acceso: c.clave };
        serie = c.serie;
      }
      const client_uuid = uuid();
      const fecha = new Date().toISOString();
      const args = {
        _cuenta: cuenta.id,
        _payload: {
          caja_id: cajaId, cliente_partner_id: cli, tipo_doc: tipoDoc,
          pagos: pagosValidos.map((p) => ({ forma_pago_id: p.forma_pago_id, monto: Number(p.monto), referencia: p.referencia || null,
              adquirente: p.adquirente || null, tarjeta_autorizacion: p.tarjeta_autorizacion || null,
              tarjeta_ultimos4: p.tarjeta_ultimos4 || null, tarjeta_marca: p.tarjeta_marca || null,
              tarjeta_lote: p.tarjeta_lote || null, tarjeta_terminal: p.tarjeta_terminal || null,
              tarjeta_voucher: p.tarjeta_voucher || null })),
          client_uuid, fecha, offline,
        },
      };
      await encolarVenta({
        client_uuid, fecha, caja_id: cajaId,
        caja_nombre: cajas.find((c) => c.id === cajaId)?.nombre ?? 'caja',
        total: totalSel, tipo_doc: tipoDoc, serie,
        rpc: 'rest_cuenta_cobrar', args, payload: args._payload,
      });
      await cobradaOfflineMarcar(cuenta.id, client_uuid);
      await imprimirTicket({
        codigo: cuenta.codigo, tipoDoc, claveAcceso: offline?.clave_acceso ?? null, serie,
        fecha: new Date().toLocaleString(LOCALE, { hour12: false }),
        cliente: 'CONSUMIDOR FINAL',
        lineas: lineas.map((l) => ({ descripcion: l.descripcion, cantidad: Number(l.cantidad),
          precio_unitario: Number(l.precio_unitario), subtotal: Number(l.subtotal) })),
        subtotal: subSel + srvSel, iva: ivaSel, total: totalSel,
        recibido: pagosValidos.reduce((a, p) => a + Number(p.monto), 0),
        vuelto: Math.max(pagosValidos.reduce((a, p) => a + Number(p.monto), 0) - totalSel, 0),
      });
      toast.success(`Cobro OFFLINE encolado (${cuenta.codigo}): se sincroniza al volver la red`);
      onDone(true);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setCobrando(false); }
  };

  const cobrar = async () => {
    if (!online) { await cobrarOffline(); return; }
    if (!cajaId) { toast.error('Elige la caja'); return; }
    if (!sesion) { toast.error('La caja no tiene un turno abierto (Apertura de caja)'); return; }
    if (dividir && !partesOn && !lineasSel.length) { toast.error('Marca las líneas a cobrar'); return; }
    setCobrando(true);
    try {
      const r = await posRpc<{ venta_id: string; codigo: string; cuenta_cerrada: boolean; servicio: number;
        parte?: number; partes?: number; lineas_pendientes?: number }>(
        'rest_cuenta_cobrar', {
          _cuenta: cuenta.id,
          _payload: {
            caja_id: cajaId, cliente_partner_id: clienteId || localStorage.getItem('pos_rest_cf') || null, tipo_doc: tipoDoc,
            lineas_ids: dividir && !partesOn ? lineasSel : null,
            partes: partesOn ? { n: nPartes } : null,
            pagos: pagos.filter((p) => Number(p.monto) > 0)
              .map((p) => ({ forma_pago_id: p.forma_pago_id, monto: Number(p.monto), referencia: p.referencia || null,
              adquirente: p.adquirente || null, tarjeta_autorizacion: p.tarjeta_autorizacion || null,
              tarjeta_ultimos4: p.tarjeta_ultimos4 || null, tarjeta_marca: p.tarjeta_marca || null,
              tarjeta_lote: p.tarjeta_lote || null, tarjeta_terminal: p.tarjeta_terminal || null,
              tarjeta_voucher: p.tarjeta_voucher || null })),
          },
        });
      toast.success(`Cobrado: ${r.codigo}${r.parte ? ` · parte ${r.parte}/${r.partes}` : ''}${r.cuenta_cerrada ? ' · cuenta cerrada' : ' · queda saldo por cobrar'}`);
      // propina voluntaria (solo arqueo, no factura)
      const prop = Number(propina) || 0;
      if (prop > 0) {
        try {
          await posRpc('pos_movimiento_caja', { payload: { sesion_id: sesion.id, tipo: 'ingreso', monto: prop, concepto: `Propina voluntaria ${r.codigo}` } });
        } catch (e) { toast.error(`Propina no registrada: ${getErrorMessage(e)}`); }
      }
      // ticket
      try {
        const t = await posRpc<TicketRpc | null>('pos_venta_ticket', { _venta_id: r.venta_id });
        if (t) await imprimirTicket(ventaATicket(t), t.empresa ?? {});
      } catch { /* reimprimible desde Ventas del día */ }
      onDone(r.cuenta_cerrada);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setCobrando(false); }
  };

  // Cargo a la habitación (módulo Hotel, 921): cuenta COMPLETA (sin dividir), sin
  // pagos — el consumo va al folio del huésped y se cobra en el check-out.
  // `cliente_partner_id` debe ser el TITULAR de la reserva, no el cliente del POS.
  // NOTA (ver reporte): `rest_cuenta_cobrar` debe reenviar `hotel_reserva_id` al
  // llamar a `pos.pos_registrar_venta` — pendiente de confirmar en el script vigente.
  const cobrarHabitacion = async () => {
    if (!online) { toast.error('Sin conexión: el cargo a la habitación necesita red'); return; }
    if (!cajaId) { toast.error('Elige la caja'); return; }
    if (!sesion) { toast.error('La caja no tiene un turno abierto (Apertura de caja)'); return; }
    if (!huespedSel) { toast.error('Seleccione la habitación / huésped'); return; }
    setCobrando(true);
    try {
      const r = await posRpc<{ venta_id: string; codigo: string; cuenta_cerrada: boolean }>(
        'rest_cuenta_cobrar', {
          _cuenta: cuenta.id,
          _payload: {
            caja_id: cajaId, cliente_partner_id: huespedSel.titular_partner_id, tipo_doc: 'habitacion',
            hotel_reserva_id: huespedSel.reserva_id, pagos: [],
          },
        });
      toast.success(`Cargo ${r.codigo} enviado a la habitación ${huespedSel.habitacion}`);
      try {
        const t = await posRpc<TicketRpc | null>('pos_venta_ticket', { _venta_id: r.venta_id });
        if (t) await imprimirTicket({ ...ventaATicket(t), habitacion: huespedSel.habitacion }, t.empresa ?? {});
      } catch { /* reimprimible desde Ventas del día */ }
      onDone(r.cuenta_cerrada);
    } catch (e) { toast.error(getErrorMessage(e)); }
    finally { setCobrando(false); }
  };

  return (
    <Modal title={`Cobrar ${cuenta.codigo}${cuenta.mesa_numero != null ? ` · Mesa ${cuenta.mesa_numero}` : ''}`} onClose={intentarCerrar}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Caja (turno abierto)">
            <select className={fieldCls} value={cajaId} onChange={(e) => setCajaId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {cajas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </select>
          </Campo>
          {modoCobro === 'pago' ? (
            <Campo label="Documento">
              <select className={fieldCls} value={tipoDoc} onChange={(e) => setTipoDoc(e.target.value as 'factura' | 'nota_venta')}>
                <option value="factura">Factura (SRI)</option>
                <option value="nota_venta">Nota de venta</option>
              </select>
            </Campo>
          ) : (
            <Campo label="Documento">
              <div className={`${fieldCls} flex items-center text-muted-foreground`}>Cargo a habitación (se factura en el check-out)</div>
            </Campo>
          )}
        </div>
        {hotelDisponible && (
          <div className="grid grid-cols-2 overflow-hidden rounded-md border text-sm">
            <button type="button" className={`py-1.5 ${modoCobro === 'pago' ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`} onClick={() => setModoCobro('pago')}>Cobro normal</button>
            <button type="button" className={`py-1.5 ${modoCobro === 'habitacion' ? 'bg-sky-600 text-white font-medium' : 'hover:bg-muted'}`} onClick={() => { setModoCobro('habitacion'); setDividir(false); setPartesOn(false); setLineasSel([]); }}>Cargar a la habitación</button>
          </div>
        )}
        {modoCobro === 'habitacion' && (
          <div className="space-y-2">
            <Campo label="Buscar por n.º de habitación o nombre del huésped">
              <input className={fieldCls} autoFocus placeholder="Ej. 204 o Pérez" value={buscaHuesped} onChange={(e) => setBuscaHuesped(e.target.value)} />
            </Campo>
            <div className="max-h-40 overflow-auto rounded-md border divide-y text-sm">
              {huespedesLoading && <div className="p-2 text-xs text-muted-foreground">Cargando huéspedes en casa…</div>}
              {!huespedesLoading && huespedesFiltrados.length === 0 && <div className="p-2 text-xs text-muted-foreground">No hay huéspedes en casa que coincidan</div>}
              {huespedesFiltrados.map((h) => (
                <button key={h.reserva_id} type="button" onClick={() => setHuespedSel(h)}
                  className={`flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left hover:bg-muted ${huespedSel?.reserva_id === h.reserva_id ? 'bg-primary/10' : ''}`}>
                  <span><b>Hab. {h.habitacion}</b> · {h.huesped} <span className="text-xs text-muted-foreground">({h.codigo})</span></span>
                  <span className="text-right text-xs text-muted-foreground">Saldo {money(h.saldo)}{h.limite_credito != null ? ` / límite ${money(h.limite_credito)}` : ''}</span>
                </button>
              ))}
            </div>
            {huespedSel && (
              <div className="rounded-md border p-2 text-xs space-y-1">
                <div className="flex justify-between"><span>Saldo actual del folio</span><span>{money(huespedSel.saldo)}</span></div>
                <div className="flex justify-between font-medium"><span>Saldo tras este cargo</span><span>{money(huespedSel.saldo + totalCompleto)}</span></div>
                {huespedSel.limite_credito != null && huespedSel.saldo + totalCompleto > huespedSel.limite_credito && (
                  <p className="text-red-600 dark:text-red-400">⚠ Supera el límite de crédito del huésped ({money(huespedSel.limite_credito)}). Puede continuar, pero avise a recepción.</p>
                )}
              </div>
            )}
          </div>
        )}
        {!online && (
          <p className="text-xs font-medium text-red-600 dark:text-red-400">
            🔌 SIN CONEXIÓN — cobro offline: cuenta completa, solo EFECTIVO; factura con clave del bloque
            reservado (o nota de venta). Se sincroniza e emite al volver la red.
          </p>
        )}
        {online && cajaId && !sesion && (
          <p className="text-xs text-red-500">Esta caja no tiene turno abierto: ábrelo en POS → Venta/Caja → Apertura.</p>
        )}
        {modoCobro === 'pago' && (
        <Campo label="Cliente (F5 para buscar)">
          <button type="button"
                  className={`${fieldCls} flex items-center justify-between gap-2 text-left`}
                  onClick={() => setBuscarCli(true)}>
            <span className="truncate">{clienteNombre || 'Seleccionar cliente…'}</span>
            <span className="shrink-0 text-xs text-muted-foreground">buscar ⌕</span>
          </button>
        </Campo>
        )}
        {buscarCli && (
          <BuscarCliente onClose={() => setBuscarCli(false)}
            onPick={(c) => {
              setClienteId(c.id);
              setClienteNombre(`${c.razon_social}${c.identificacion ? ` (${c.identificacion})` : ''}`);
              setBuscarCli(false);
            }} />
        )}

        {modoCobro === 'pago' && (
        <div className={`flex items-center gap-4 flex-wrap ${!online ? 'opacity-40 pointer-events-none' : ''}`}>
          <label className={`flex items-center gap-2 text-sm cursor-pointer ${cuenta.partes ? 'opacity-40 pointer-events-none' : ''}`}>
            <input type="checkbox" className="h-4 w-4" checked={dividir && !partesOn}
                   onChange={(e) => { setDividir(e.target.checked); setPartesOn(false); setLineasSel([]); }} />
            Dividir por ítem
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="h-4 w-4" checked={partesOn} disabled={!!cuenta.partes}
                   onChange={(e) => { setPartesOn(e.target.checked); setDividir(false); setLineasSel([]); }} />
            Dividir en partes IGUALES
          </label>
          {partesOn && (
            <span className="flex items-center gap-2 text-sm">
              <input className={`${fieldCls} !w-16 text-center`} type="number" min="2" max="20"
                     value={partesN} disabled={!!cuenta.partes}
                     onChange={(e) => setPartesN(e.target.value)} />
              partes · cobrando la <span className="font-semibold">parte {parteActual} de {nPartes}</span>
            </span>
          )}
        </div>
        )}
        {dividir && !partesOn && (
          <>
            {lineas.some((l) => l.asiento != null) && (
              <div className="flex items-center gap-1.5 text-xs flex-wrap">
                <span className="text-muted-foreground">Marcar por asiento:</span>
                {[...new Set(lineas.filter((l) => l.asiento != null).map((l) => l.asiento as number))].sort((a, b) => a - b).map((a) => (
                  <button key={a} className="px-2 py-0.5 rounded-full border"
                          onClick={() => setLineasSel(lineas.filter((l) => l.asiento === a).map((l) => l.id))}>
                    Asiento {a}
                  </button>
                ))}
              </div>
            )}
            <div className="max-h-40 overflow-auto border rounded-md divide-y text-sm">
              {lineas.map((l) => (
                <label key={l.id} className="flex items-center gap-2 px-2 py-1 cursor-pointer">
                  <input type="checkbox" checked={lineasSel.includes(l.id)}
                         onChange={(e) => setLineasSel((s) => e.target.checked ? [...s, l.id] : s.filter((x) => x !== l.id))} />
                  <span className="flex-1">{l.cantidad}× {l.descripcion}{l.asiento != null && <span className="text-xs text-primary"> (A{l.asiento})</span>}</span>
                  <span className="font-mono">{money(l.subtotal)}</span>
                </label>
              ))}
            </div>
          </>
        )}

        <div className="text-sm space-y-0.5 border rounded-md p-2 bg-muted/30">
          {partesOn ? (
            <>
              <div className="flex justify-between"><span>Total de la cuenta</span><span className="font-mono">{money(cuenta.totales.total)}</span></div>
              <div className="flex justify-between font-bold"><span>Parte {parteActual}/{nPartes} a cobrar</span><span className="font-mono">{money(totalSel)}</span></div>
            </>
          ) : (
            <>
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{money(subSel)}</span></div>
              {srvSel > 0 && <div className="flex justify-between"><span>Servicio {cuenta.servicio_pct}%</span><span className="font-mono">{money(srvSel)}</span></div>}
              <div className="flex justify-between"><span>IVA</span><span className="font-mono">{money(ivaSel)}</span></div>
              <div className="flex justify-between font-bold"><span>TOTAL a cobrar</span><span className="font-mono">{money(totalSel)}</span></div>
            </>
          )}
        </div>

        {modoCobro === 'pago' && pagos.map((p, i) => (
          <div key={p.key} className="grid grid-cols-[1fr_110px_1fr_auto] gap-2 items-end">
            <Campo label={i === 0 ? 'Forma de pago' : ''}>
              <select className={fieldCls} value={p.forma_pago_id}
                      onChange={(e) => setPagos((ps) => ps.map((x) => x.key === p.key ? { ...x, forma_pago_id: e.target.value } : x))}>
                {formas.map((f) => <option key={f.id} value={f.id}>{f.nombre}</option>)}
              </select>
            </Campo>
            <Campo label={i === 0 ? 'Monto' : ''}>
              <input className={`${fieldCls} text-right font-mono`} type="number" step="0.01" value={p.monto}
                     onChange={(e) => setPagos((ps) => ps.map((x) => x.key === p.key ? { ...x, monto: e.target.value } : x))} />
            </Campo>
            <Campo label={i === 0 ? 'Referencia' : ''}>
              <input className={fieldCls} value={p.referencia}
                     onChange={(e) => setPagos((ps) => ps.map((x) => x.key === p.key ? { ...x, referencia: e.target.value } : x))} />
            </Campo>
            <Button size="sm" variant="ghost" onClick={() => setPagos((ps) => ps.length > 1 ? ps.filter((x) => x.key !== p.key) : ps)}>✕</Button>
            {modosPasarela?.enlace && modosPasarela.forma_pago_id === p.forma_pago_id && (
              <div className="col-span-4 flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={(Number(p.monto) || 0) <= 0}
                        onClick={() => setPasarelaPagoKey(p.key)}>
                  Cobrar con tarjeta (QR en la mesa)
                </Button>
                {p.referencia && <span className="text-xs text-emerald-700 dark:text-emerald-400">✓ Pagado · {p.referencia}</span>}
              </div>
            )}
          </div>
        ))}
        {modoCobro === 'pago' && (
        <div className="flex items-center justify-between">
          <Button size="sm" variant="outline"
                  onClick={() => setPagos((ps) => [...ps, { key: Date.now(), forma_pago_id: formas[0]?.id ?? '', monto: '', referencia: '' }])}>
            + Otro pago
          </Button>
          <Campo label="Propina voluntaria (no factura, va al arqueo)">
            <input className={`${fieldCls} text-right font-mono !w-32`} type="number" step="0.01" min="0"
                   value={propina} onChange={(e) => setPropina(e.target.value)} />
          </Campo>
        </div>
        )}

        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={intentarCerrar} disabled={hayCobroPasarela}
                  title={hayCobroPasarela ? AVISO_NO_SALIR : undefined}>Cancelar</Button>
          {modoCobro === 'habitacion' ? (
            <Button onClick={() => void cobrarHabitacion()} isLoading={cobrando}
                    disabled={!cajaId || !sesion || !huespedSel}>
              Cargar a Hab. {huespedSel?.habitacion ?? '—'} · {money(totalSel)}
            </Button>
          ) : (
            <Button onClick={() => void cobrar()} isLoading={cobrando}
                    disabled={!cajaId || (online && !sesion)}>
              {online ? 'Cobrar' : '🔌 Cobrar OFFLINE'} {money(totalSel)}
            </Button>
          )}
        </div>
        {pasarelaPagoKey !== null && (
          <CobroPasarela
            // Con el recargo por comisión de tarjeta incluido: es lo que el
            // cliente debe. El salón no lo mostraba, pero pos_registrar_venta
            // lo suma igual, así que cobrar la base dejaba el recargo sin cobrar.
            monto={(() => {
              const pg = pagos.find((x) => x.key === pasarelaPagoKey);
              if (!pg) return 0;
              const pct = Number(formas.find((f) => f.id === pg.forma_pago_id)?.comision_pct) || 0;
              const base = Number(pg.monto) || 0;
              return base + (pct > 0 ? redondearMoneda((base * pct) / 100, 'USD') : 0);
            })()}
            modulo="restaurant"
            cajaId={cajaId}
            partnerId={clienteId || null}
            moneda="USD"
            onPagado={(ref, extra) => {
              setPagos((ps) => ps.map((x) => x.key === pasarelaPagoKey
                ? { ...x, referencia: ref, adquirente: extra.adquirente,
                    tarjeta_autorizacion: extra.autorizacion ?? '',
                    tarjeta_ultimos4: extra.ultimos4 ?? '', tarjeta_marca: extra.marca ?? '',
                    tarjeta_lote: extra.lote ?? '', tarjeta_terminal: extra.terminal ?? '',
                    tarjeta_voucher: ref, pasarela_ok: true }
                : x));
              setPasarelaPagoKey(null);
            }}
            onClose={() => setPasarelaPagoKey(null)}
          />
        )}
      </div>
    </Modal>
  );
}
